import { Badge, Button, EmptyStateHelp, LearnMoreLink } from '@auctisync/ui';
import { AUCTION_ROW_COLUMNS, type AuctionRow } from '../../lib/rowColumns';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type Auction = AuctionRow;

const PAGE_SIZE = 25;
const STATUSES = [
  'draft',
  'queued',
  'preview',
  'active',
  'closing',
  'completed',
  'archived',
  'cancelled',
] as const;
const TYPES = [
  'timed_online',
  'live_simulcast',
  'live_floor',
  'fundraiser',
  'private_treaty',
] as const;

/**
 * Auction list view. Server-paginated, searchable, filterable.
 */
export function AdminAuctionList() {
  const [rows, setRows] = useState<Auction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      let query = supabase
        .from('auctions')
        .select(AUCTION_ROW_COLUMNS, { count: 'exact' })
        .order('created_at', { ascending: false });

      if (statusFilter) query = query.eq('status', statusFilter);
      if (typeFilter) query = query.eq('auction_type', typeFilter);

      const trimmed = search.trim();
      if (trimmed) {
        const escaped = trimmed.replace(/[%_,]/g, (c) => `\\${c}`);
        const pattern = `%${escaped}%`;
        query = query.or(`title.ilike.${pattern},slug.ilike.${pattern}`);
      }

      query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, count, error: err } = await query;
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRows([]);
        setTotalCount(0);
      } else {
        setRows(data ?? []);
        setTotalCount(count ?? 0);
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [search, statusFilter, typeFilter, page]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasResults = rows.length > 0;
  const hasFilters = Boolean(search || statusFilter || typeFilter);

  const rangeLabel = useMemo(() => {
    if (totalCount === 0) return '0 auctions';
    const start = page * PAGE_SIZE + 1;
    const end = Math.min((page + 1) * PAGE_SIZE, totalCount);
    return `${start}–${end} of ${totalCount}`;
  }, [page, totalCount]);

  return (
    <div>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          marginBottom: '1.25rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Auctions
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.35rem' }}>
            Your scheduled sale events. Lots live inside auctions.
          </p>
          <LearnMoreLink helpSlug="auction-overview" newTab>
            How auctions work
          </LearnMoreLink>
        </div>
        <Button asChild>
          <Link to="/admin/auctions/new">+ New auction</Link>
        </Button>
      </header>

      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: '1rem',
          padding: '0.75rem',
          background: 'var(--surface-elev)',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
        }}
      >
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search title or slug…"
          style={{
            flex: '1 1 18rem',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: '0.875rem',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          style={selectStyle}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(0);
          }}
          style={selectStyle}
          aria-label="Filter by auction type"
        >
          <option value="">All types</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          Failed to load auctions: {error}
        </div>
      )}

      {!loading && !hasResults && !hasFilters && (
        <EmptyStateHelp
          title="No auctions yet"
          description="Create your first auction to start scheduling sales. Pull lots in from your inventory."
          helpSlug="auction-overview"
          primaryActionLabel="Create an auction"
          primaryActionHref="/admin/auctions/new"
        />
      )}

      {(loading || hasResults) && (
        <div
          style={{
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-card, 0.5rem)',
            overflow: 'hidden',
            background: 'var(--surface-elev)',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr
                  style={{
                    background: 'var(--surface-muted)',
                    borderBottom: '1px solid var(--admin-hairline, var(--border))',
                  }}
                >
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Starts</th>
                  <th style={thStyle}>Ends</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Lots</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? [0, 1, 2, 3, 4].map((i) => (
                      <tr
                        key={i}
                        style={{ borderBottom: '1px solid var(--admin-hairline, var(--border))' }}
                      >
                        {[0, 1, 2, 3, 4, 5].map((j) => (
                          <td key={j} style={tdStyle}>
                            <span
                              style={{
                                display: 'inline-block',
                                height: '0.875rem',
                                width: '60%',
                                background: 'var(--surface-muted)',
                                borderRadius: '0.25rem',
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  : rows.map((row) => (
                      <tr
                        key={row.id}
                        style={{ borderBottom: '1px solid var(--admin-hairline, var(--border))' }}
                      >
                        <td style={tdStyle}>
                          <Link
                            to={`/admin/auctions/${row.id}`}
                            style={{ color: 'var(--brand-accent)' }}
                          >
                            {row.title}
                          </Link>
                          {row.reference_number !== null && (
                            <span
                              style={{
                                marginLeft: '0.5rem',
                                fontSize: '0.75rem',
                                color: 'var(--text-muted)',
                                fontFamily: 'var(--font-mono, monospace)',
                              }}
                            >
                              #{row.reference_number}
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {row.auction_type.replace(/_/g, ' ')}
                        </td>
                        <td style={tdStyle}>
                          <StatusBadge status={row.status} />
                        </td>
                        <td
                          style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}
                        >
                          {row.starts_at ? formatDateShort(row.starts_at) : '—'}
                        </td>
                        <td
                          style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}
                        >
                          {row.ends_at ? formatDateShort(row.ends_at) : '—'}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {row.lot_count}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(hasResults || totalCount > PAGE_SIZE) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
          }}
        >
          <span>{rangeLabel}</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={pagerButtonStyle(page === 0)}
            >
              ← Prev
            </button>
            <span style={{ alignSelf: 'center' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              style={pagerButtonStyle(page >= totalPages - 1)}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active' || status === 'closing') return <Badge variant="success">{status}</Badge>;
  if (status === 'cancelled' || status === 'archived')
    return <Badge variant="outline">{status}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  verticalAlign: 'middle',
};

const selectStyle: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  minWidth: '11rem',
};

function pagerButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '0.4rem 0.75rem',
    border: '1px solid var(--admin-hairline, var(--border))',
    borderRadius: 'var(--r-tile, 0.375rem)',
    background: 'var(--surface)',
    color: disabled ? 'var(--text-muted)' : 'var(--text)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    fontSize: '0.875rem',
  };
}
